import type { ReactNode } from "react";
import { FileScan as FileScanIcon, FileText as FileTextIcon, MessageSquare as MessageSquareIcon, Users as UsersIcon } from "@tamagui/lucide-icons";
import { Badge, Body1, Body2, BrandGraphic, Card, Subtitle2, Title3, XStack, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
export type FaResultCatastrophicOutcomes = {
  continue: {};
};
const Outcome = tagged<FaResultCatastrophicOutcomes>();
const MORE_WAYS_TO_SAVE = [{
  icon: <FileScanIcon size="$iconSmall" color="$iconDefault" />,
  title: "Bill checker",
  description: "Catch errors & overcharges"
}, {
  icon: <UsersIcon size="$iconSmall" color="$iconDefault" />,
  title: "Negotiation",
  description: "We'll fight to lower your bills"
}, {
  icon: <FileTextIcon size="$iconSmall" color="$iconDefault" />,
  title: "File claims",
  description: "We'll file your out-of-network claims"
}, {
  icon: <MessageSquareIcon size="$iconSmall" color="$iconDefault" />,
  title: "Benefits Q&A",
  description: "Understand your insurance"
}] as const;
function MoreWayToSaveRow({
  icon,
  title,
  description
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return <XStack gap="$sp1.5" alignItems="center" data-sentry-element="XStack" data-sentry-component="MoreWayToSaveRow" data-sentry-source-file="FaResultCatastrophic.tsx">
      <YStack width={32} height={32} borderRadius="$br1" backgroundColor="$surface0" alignItems="center" justifyContent="center" data-sentry-element="YStack" data-sentry-source-file="FaResultCatastrophic.tsx">
        {icon}
      </YStack>
      <YStack flex={1} data-sentry-element="YStack" data-sentry-source-file="FaResultCatastrophic.tsx">
        <Subtitle2 color="$textDefault" data-sentry-element="Subtitle2" data-sentry-source-file="FaResultCatastrophic.tsx">{title}</Subtitle2>
        <Body2 color="$textSubtle" data-sentry-element="Body2" data-sentry-source-file="FaResultCatastrophic.tsx">{description}</Body2>
      </YStack>
    </XStack>;
}
export function FaResultCatastrophic({
  onDone
}: {
  onDone: OnDone<FaResultCatastrophicOutcomes>;
}) {
  return <OnboardingScreen showBackgroundGradient data-sentry-element="OnboardingScreen" data-sentry-component="FaResultCatastrophic" data-sentry-source-file="FaResultCatastrophic.tsx">
      <OnboardingScreen.Nav showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="FaResultCatastrophic.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="FaResultCatastrophic.tsx">
        <OnboardingScreen.Header alignment="left" title="You're still covered" subtitle="You may not qualify for income-based discounts but Granted can still help you save." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="FaResultCatastrophic.tsx" />
        <Card variant="elevated" size="large" data-sentry-element="Card" data-sentry-source-file="FaResultCatastrophic.tsx">
          <YStack gap="$sp1" data-sentry-element="YStack" data-sentry-source-file="FaResultCatastrophic.tsx">
            <XStack justifyContent="space-between" alignItems="flex-start" gap="$sp2" data-sentry-element="XStack" data-sentry-source-file="FaResultCatastrophic.tsx">
              <YStack gap="$sp1" alignItems="flex-start" flexShrink={1} data-sentry-element="YStack" data-sentry-source-file="FaResultCatastrophic.tsx">
                <Badge status="positive" size="small" data-sentry-element="Badge" data-sentry-source-file="FaResultCatastrophic.tsx">
                  You're protected
                </Badge>
                <Title3 data-sentry-element="Title3" data-sentry-source-file="FaResultCatastrophic.tsx">Major bill relief</Title3>
              </YStack>
              <BrandGraphic type="bill" indicator="dollar" size="$si7" data-sentry-element="BrandGraphic" data-sentry-source-file="FaResultCatastrophic.tsx" />
            </XStack>
            <Body1 color="$textSubtle" data-sentry-element="Body1" data-sentry-source-file="FaResultCatastrophic.tsx">
              Many hospitals give large discounts on large bills. We'll take
              care of the application for you.
            </Body1>
          </YStack>
        </Card>
        <YStack gap="$sp2" paddingTop="$sp3" data-sentry-element="YStack" data-sentry-source-file="FaResultCatastrophic.tsx">
          <Subtitle2 color="$textSubtle" data-sentry-element="Subtitle2" data-sentry-source-file="FaResultCatastrophic.tsx">More ways to save</Subtitle2>
          <YStack gap="$sp2" data-sentry-element="YStack" data-sentry-source-file="FaResultCatastrophic.tsx">
            {MORE_WAYS_TO_SAVE.map(row => <MoreWayToSaveRow key={row.title} icon={row.icon} title={row.title} description={row.description} />)}
          </YStack>
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="FaResultCatastrophic.tsx">
        <OnboardingScreen.PrimaryAction testID="fa-result-catastrophic.activate" variant="brand" onPress={() => onDone(Outcome.continue())} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="FaResultCatastrophic.tsx">
          Continue
        </OnboardingScreen.PrimaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}